import { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

interface CtraderAccount {
  id: number;
  account_id: string;
  account_name: string;
  balance: number;
  currency: string;
  is_live: boolean;
}

interface AccountTabsProps {
  accounts: CtraderAccount[];
  selectedAccountId: string | null;
  strategyCounts: Record<string, number>;
  onSelect: (accountId: string) => void;
  isGatewayConnected: boolean;
}

export default function AccountTabs({
  accounts,
  selectedAccountId,
  strategyCounts,
  onSelect,
  isGatewayConnected,
}: AccountTabsProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const checkScroll = () => {
      setShowLeftFade(el.scrollLeft > 5);
      setShowRightFade(el.scrollLeft + el.clientWidth < el.scrollWidth - 5);
    };
    checkScroll();
    el.addEventListener('scroll', checkScroll, { passive: true });
    window.addEventListener('resize', checkScroll);
    return () => {
      el.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, [accounts]);

  if (accounts.length === 0) {
    return (
      <div className="mb-4">
        <div className="flex items-center gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200/70">
          <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
            <i className="ri-plug-line text-amber-500 text-lg"></i>
          </div>
          <div className="flex-1">
            <p className="text-sm font-heading font-semibold text-amber-800">No cTrader Accounts Connected</p>
            <p className="text-xs text-amber-600 mt-0.5">
              Connect your cTrader accounts through the gateway to start managing strategies.
            </p>
          </div>
          <Link
            to="/dashboard"
            className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-background-50 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap"
          >
            Go to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-5 relative">
      {/* Left fade */}
      {showLeftFade && (
        <div className="absolute left-0 top-0 bottom-0 w-10 bg-gradient-to-r from-background-50 to-transparent z-10 pointer-events-none rounded-l-xl"></div>
      )}

      {/* Tabs */}
      <div
        ref={scrollRef}
        className="flex items-center gap-2 overflow-x-auto scrollbar-hide px-1 pb-1"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {accounts.map((acc) => {
          const isSelected = selectedAccountId === acc.account_id;
          const count = strategyCounts[acc.account_id] || 0;

          return (
            <button
              key={acc.account_id}
              onClick={() => onSelect(acc.account_id)}
              className={`group flex items-center gap-2.5 px-4 py-2.5 rounded-xl border transition-all duration-200 cursor-pointer shrink-0 whitespace-nowrap ${
                isSelected
                  ? 'bg-primary-500 border-primary-500 text-background-50 shadow-sm'
                  : 'bg-background-100 border-background-200/70 text-foreground-700 hover:border-background-300/60 hover:bg-background-200/50'
              }`}
            >
              {/* Account icon */}
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${
                isSelected ? 'bg-background-50/20' : 'bg-background-200/70'
              }`}>
                <i className={`ri-user-3-line text-sm ${
                  isSelected ? 'text-background-50' : 'text-foreground-500'
                }`}></i>
              </div>

              {/* Info */}
              <div className="text-left">
                <div className="flex items-center gap-1.5">
                  <span className={`text-xs font-heading font-semibold ${
                    isSelected ? 'text-background-50' : 'text-foreground-900'
                  }`}>
                    {acc.account_name}
                  </span>
                  <span className={`w-1.5 h-1.5 rounded-full ${
                    acc.is_live ? 'bg-emerald-400' : 'bg-foreground-300'
                  } ${isSelected ? '!bg-background-50' : ''}`}></span>
                </div>
                <div className={`text-[10px] font-mono ${
                  isSelected ? 'text-background-50/70' : 'text-foreground-500'
                }`}>
                  {acc.account_id}
                </div>
              </div>

              {/* Strategy count badge */}
              {count > 0 && (
                <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold ${
                  isSelected
                    ? 'bg-background-50/20 text-background-50'
                    : 'bg-primary-500/10 text-primary-400'
                }`}>
                  {count}
                </span>
              )}

              {/* Selected indicator */}
              {isSelected && (
                <i className="ri-arrow-down-s-line text-background-50/70 text-xs ml-0.5"></i>
              )}
            </button>
          );
        })}
      </div>

      {/* Right fade */}
      {showRightFade && (
        <div className="absolute right-0 top-0 bottom-0 w-10 bg-gradient-to-l from-background-50 to-transparent z-10 pointer-events-none rounded-r-xl"></div>
      )}
    </div>
  );
}